import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAdminBillingAccountQueryFn } from 'app/queries/billing/createFetchAdminBillingAccountQueryFn';
import { useState } from 'react';
import { RateField } from './components/RateField';
import { buildRatesState } from './helpers/buildRatesState';
import { buildSetBillingAccountRatesRequestBody } from './helpers/buildSetBillingAccountRatesRequestBody';
import { hasEditableRateAmount } from './helpers/hasEditableRateAmount';
import { parseFullUnlockChargeType } from './helpers/parseFullUnlockChargeType';
import { resolveErrorMessage } from './helpers/resolveErrorMessage';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';
import { TRatesState } from './types/TRatesState';

const LOAD_ERROR_MESSAGE = 'Failed to load billing rates. Please try again.';
const SAVE_ERROR_MESSAGE = 'Failed to save billing rates. Please try again.';

const fullUnlockChargeTypeOptions = [
  { label: 'Pay difference', value: 'PAY_DIFFERENCE' },
  { label: 'Additional charge', value: 'ADDITIONAL_CHARGE' },
];

export const AdminBillingAccountRatesEditorModal = ({ billingAccountId, onCancel, onSuccess }: TProps) => {
  const { closeModal } = useModalContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [editedRates, setEditedRates] = useState<TRatesState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const billingAccountQuery = useQuery({
    queryKey: billingKeys.adminAccount(billingAccountId),
    queryFn: createFetchAdminBillingAccountQueryFn(call),
  });
  const billingAccount = billingAccountQuery.data;
  const rates = editedRates ?? buildRatesState(billingAccount?.billingConfiguration);

  const updateRates = (patch: Partial<TRatesState>) => setEditedRates({ ...rates, ...patch });

  const saveMutation = useMutation({
    mutationFn: () =>
      call(() =>
        AdminService.setBillingAccountRates({
          baId: billingAccountId,
          requestBody: buildSetBillingAccountRatesRequestBody(rates),
        }),
      ),
    onSuccess: (result) => {
      if (!result?.success) {
        setSaveError(result?.error ?? result?.message ?? SAVE_ERROR_MESSAGE);
        return;
      }
      closeModal();
      onSuccess();
    },
    onError: (mutationError) => setSaveError(resolveErrorMessage(mutationError, SAVE_ERROR_MESSAGE)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingKeys.adminAccount(billingAccountId) }),
  });

  const onSubmit = () => {
    setSaveError(null);
    saveMutation.mutate();
  };

  const loadError = billingAccountQuery.isError
    ? resolveErrorMessage(billingAccountQuery.error, LOAD_ERROR_MESSAGE)
    : null;
  const errorMessage = saveError ?? loadError;
  const isSaving = saveMutation.isPending;
  const canSave = billingAccountQuery.isSuccess && !isSaving;

  return (
    <ModalWrapper width="400px">
      {billingAccountQuery.isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>AutoScript Rates Editor</ModalTitle>
      <Content>
        {errorMessage ? <GenericPageError message={errorMessage} /> : null}

        <div className="input-wrapper">
          <div className="label">Billing Account</div>
          <div className="name">{billingAccount?.name}</div>
        </div>

        <div className="input-wrapper">
          <div className="label">Organization</div>
          <div className="name">{billingAccount?.organization?.name}</div>
        </div>

        <div className="flex-row flex gap-2 items-top">
          <RateField
            label="Post Processing Rate"
            name="postProcessingRateType"
            testId="post-processing-rate-select"
            rateType={rates.postProcessingRateType}
            rate={rates.postProcessingRate}
            disabled={isSaving}
            onRateTypeChange={(postProcessingRateType) => updateRates({ postProcessingRateType })}
            onRateChange={(postProcessingRate) => updateRates({ postProcessingRate })}
          />
          <RateField
            label="Real-Time Rate"
            name="realTimeRateType"
            testId="real-time-rate-select"
            rateType={rates.realTimeRateType}
            rate={rates.realTimeRate}
            disabled={isSaving}
            onRateTypeChange={(realTimeRateType) => updateRates({ realTimeRateType })}
            onRateChange={(realTimeRate) => updateRates({ realTimeRate })}
          />
        </div>

        <div className="flex-row flex gap-2 items-top">
          <RateField
            label="Partial Unlock Rate"
            name="partialUnlockRateType"
            testId="partial-unlock-rate-select"
            rateType={rates.partialUnlockRateType}
            rate={rates.partialUnlockRate}
            disabled={isSaving}
            onRateTypeChange={(partialUnlockRateType) => updateRates({ partialUnlockRateType })}
            onRateChange={(partialUnlockRate) => updateRates({ partialUnlockRate })}
          />
          <RateField
            label="Full Unlock Rate"
            name="fullUnlockRateType"
            testId="full-unlock-rate-select"
            rateType={rates.fullUnlockRateType}
            rate={rates.fullUnlockRate}
            disabled={isSaving}
            onRateTypeChange={(fullUnlockRateType) => updateRates({ fullUnlockRateType })}
            onRateChange={(fullUnlockRate) => updateRates({ fullUnlockRate })}
          >
            <Select
              name="fullUnlockChargeType"
              label="Charge type"
              value={rates.fullUnlockChargeType}
              onChange={(_, value) => {
                const fullUnlockChargeType = parseFullUnlockChargeType(value);
                if (fullUnlockChargeType) {
                  updateRates({ fullUnlockChargeType });
                }
              }}
              options={fullUnlockChargeTypeOptions}
              required={false}
              disabled={isSaving || !hasEditableRateAmount(rates.fullUnlockRateType)}
              testId="full-unlock-charge-type-select"
            />
          </RateField>
        </div>

        <div className="flex-row flex gap-2 items-top">
          <RateField
            label="Enhance on Demand Rate"
            name="enhancedOnDemandRateType"
            testId="enhanced-on-demand-rate-select"
            rateType={rates.enhancedOnDemandRateType}
            rate={rates.enhancedOnDemandRate}
            disabled={isSaving}
            onRateTypeChange={(enhancedOnDemandRateType) => updateRates({ enhancedOnDemandRateType })}
            onRateChange={(enhancedOnDemandRate) => updateRates({ enhancedOnDemandRate })}
          />
          <RateField
            label="Attorneys Connect Rate"
            name="attorneyConnectFeeRateType"
            testId="attorney-connect-fee-rate-select"
            rateType={rates.attorneyConnectFeeRateType}
            rate={rates.attorneyConnectFeeRate}
            disabled={isSaving}
            rateTypeLocked
            onRateTypeChange={(attorneyConnectFeeRateType) => updateRates({ attorneyConnectFeeRateType })}
            onRateChange={(attorneyConnectFeeRate) => updateRates({ attorneyConnectFeeRate })}
          />
        </div>

        <div className="flex-row flex gap-2 items-top">
          <RateField
            label="Attorneys Invite Rate"
            name="attorneyInviteFeeRateType"
            testId="attorney-invite-fee-rate-select"
            rateType={rates.attorneyInviteFeeRateType}
            rate={rates.attorneyInviteFeeRate}
            disabled={isSaving}
            rateTypeLocked
            onRateTypeChange={(attorneyInviteFeeRateType) => updateRates({ attorneyInviteFeeRateType })}
            onRateChange={(attorneyInviteFeeRate) => updateRates({ attorneyInviteFeeRate })}
          />
        </div>

        <ModalFooter>
          <OutlinedButton onClick={onCancel} data-testid="edit-billing-rates-cancel-button">
            Cancel
          </OutlinedButton>
          <Button disabled={!canSave} onClick={onSubmit} data-testid="edit-billing-rates-save-button">
            Save
          </Button>
        </ModalFooter>
      </Content>
    </ModalWrapper>
  );
};
